/**
 * utils/uuid.ts
 * Cross-browser compatible UUID generation utility
 * 
 * Provides fallback for environments where crypto.randomUUID is not available
 * (e.g., older mobile browsers, non-secure contexts)
 */

/**
 * Generate a v4 UUID with cross-browser compatibility
 * 
 * Attempts to use crypto.randomUUID first, falls back to secure alternative
 * 
 * @returns A v4 UUID string
 */
export function generateUUID(): string {
  // Try native crypto.randomUUID if available (modern browsers)
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    try {
      return crypto.randomUUID();
    } catch (error) {
      // Fall through to fallback if randomUUID fails
      console.warn('crypto.randomUUID failed, using fallback:', error);
    }
  }

  // Fallback for older browsers or non-secure contexts
  // Uses crypto.getRandomValues if available, otherwise Math.random
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    // Use crypto.getRandomValues for cryptographically secure randomness
    const array = new Uint8Array(16);
    crypto.getRandomValues(array);
    
    // Set version (4) and variant bits according to RFC 4122
    array[6] = ((array[6] ?? 0) & 0x0f) | 0x40; // Version 4
    array[8] = ((array[8] ?? 0) & 0x3f) | 0x80; // Variant bits
    
    // Convert to hex string with hyphens
    const hex = Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20, 32)}`;
  }

  // Last resort: Math.random fallback (not cryptographically secure)
  console.warn('Using Math.random for UUID generation - not cryptographically secure');
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const random = Math.random() * 16 | 0;
    const value = char === 'x' ? random : (random & 0x3 | 0x8);
    return value.toString(16);
  });
}